/**
 * TenantContext — Global State for Super Admin Tenant Selection
 *
 * Manages tenant selection for super admins navigating between tenants.
 * Uses React Context API with localStorage persistence for cross-session state.
 *
 * Features:
 * - Global tenant selection state
 * - localStorage persistence (key: "selectedTenantId")
 * - Global view mode for aggregated metrics
 * - Convenience hooks for common queries
 * - BroadcastChannel integration for cross-tab sync (handled by useTenantBroadcast)
 * - Auto-selects user's default tenant for non-superadmins
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

/**
 * Resolve estado inicial lendo do localStorage de forma segura.
 */
const getInitialTenantState = () => {
  // Evita acesso a window em ambientes sem DOM (SSR/tests sem jsdom)
  if (typeof window === 'undefined') {
    return { initialId: null, initialGlobal: true } as const;
  }

  try {
    const stored = localStorage.getItem('selectedTenantId');
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed)) {
        return { initialId: parsed, initialGlobal: false } as const;
      }
    }
  } catch (error) {
    console.error('Failed to load selectedTenantId from localStorage:', error);
  }

  return { initialId: null, initialGlobal: true } as const;
};

/**
 * Tenant context shape
 */
interface TenantContextType {
  selectedTenantId: number | null;
  isInGlobalView: boolean;
  setSelectedTenantId: (id: number | null) => void;
  setGlobalView: (isGlobal: boolean) => void;
}

/**
 * Create context with undefined default (requires provider)
 */
const TenantContext = createContext<TenantContextType | undefined>(undefined);

/**
 * TenantProvider component — wrap app root with this
 *
 * Props: None (uses React children)
 *
 * Example:
 * ```tsx
 * <TenantProvider>
 *   <App />
 * </TenantProvider>
 * ```
 */
export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { initialId, initialGlobal } = getInitialTenantState();
  const [selectedTenantId, setSelectedTenantIdState] = useState<number | null>(initialId);
  const [isInGlobalView, setIsInGlobalViewState] = useState<boolean>(initialGlobal);

  /**
   * Auto-set tenant from user context if not a superadmin and not already set
   */
  useEffect(() => {
    if (user && !user.is_superadmin && user.tenant_id && selectedTenantId === null) {
      // User is not a superadmin and has a tenant_id from JWT
      // Automatically select their tenant if not already selected
      setSelectedTenantIdState(user.tenant_id);
      localStorage.setItem('selectedTenantId', user.tenant_id.toString());
      setIsInGlobalViewState(false);
    }
  }, [user, selectedTenantId]);

  /**
   * Update selected tenant and persist to localStorage
   */
  const setSelectedTenantId = (id: number | null) => {
    setSelectedTenantIdState(id);
    if (id === null) {
      localStorage.removeItem('selectedTenantId');
      setIsInGlobalViewState(true);
    } else {
      localStorage.setItem('selectedTenantId', id.toString());
      setIsInGlobalViewState(false);
    }
  };

  /**
   * Set global view mode (view aggregated data across all tenants)
   */
  const setGlobalView = (isGlobal: boolean) => {
    if (isGlobal) {
      setSelectedTenantIdState(null);
      localStorage.removeItem('selectedTenantId');
      setIsInGlobalViewState(true);
    }
  };

  const value: TenantContextType = {
    selectedTenantId,
    isInGlobalView,
    setSelectedTenantId,
    setGlobalView,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to access full tenant context
 *
 * @throws Error if used outside TenantProvider
 * @returns TenantContextType with all state and setters
 *
 * Example:
 * ```tsx
 * const { selectedTenantId, setSelectedTenantId } = useTenantContext();
 * ```
 */
export const useTenantContext = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenantContext must be used within TenantProvider');
  }
  return context;
};

/**
 * Convenience hook to get selected tenant ID
 *
 * @returns selected tenant ID or null if in global view
 *
 * Example:
 * ```tsx
 * const tenantId = useSelectedTenantId();
 * if (tenantId) {
 *   // fetch tenant-specific data
 * }
 * ```
 */
export const useSelectedTenantId = (): number | null => {
  const { selectedTenantId } = useTenantContext();
  return selectedTenantId;
};

/**
 * Convenience hook to check if in global view mode
 *
 * @returns true if viewing aggregated data across all tenants
 *
 * Example:
 * ```tsx
 * const isGlobal = useIsGlobalView();
 * if (isGlobal) {
 *   // show global metrics
 * }
 * ```
 */
export const useIsGlobalView = (): boolean => {
  const { isInGlobalView } = useTenantContext();
  return isInGlobalView;
};

export default TenantContext;
